import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Popover from '@radix-ui/react-popover'
import { LogOut, ShoppingCart, User } from 'lucide-react'
import { useSessionStore, useCartStore } from '@/stores'
import { useIsCompact } from '@/hooks/useDevice'
import Language from '@components/Language'
import Cart from '@components/Cart'
import CheckoutCustomerConfirmation from '@components/CheckoutCustomerConfirmation'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { useUIStore } from '@/stores'
import { CustomerPortalInputType, InputState } from '@/constants'
import { isEmbedded } from '@utils/iframe'
import { useEmbedConfig } from '@/hooks/useEmbedConfig'
import { currentSessionHome } from '@/router/portalRegistry'

interface HeaderProps {
  className?: string
}

type CartTriggerButtonProps = {
  totalCartQuantity: number
  onClick?: () => void
} & React.ComponentPropsWithoutRef<typeof motion.button>

const CartTriggerButton = React.forwardRef<HTMLButtonElement, CartTriggerButtonProps>(
  ({ totalCartQuantity, onClick, ...rest }, ref) => (
    <motion.button
      ref={ref}
      type="button"
      className="relative p-1.5 cursor-pointer no-transform"
      style={{ background: 'none', border: 'none' }}
      aria-label="Shopping cart"
      onClick={onClick}
      initial="rest"
      animate="rest"
      whileHover="hover"
      whileTap="tap"
      variants={{
        rest: { scale: 1, rotate: 0 },
        hover: {
          scale: 1.1,
          rotate: [0, -10, 8, -5, 3, 0],
          transition: {
            scale: { type: 'spring', stiffness: 500, damping: 18 },
            rotate: { duration: 0.55, ease: [0.34, 1.56, 0.64, 1] as const },
          },
        },
        tap: { scale: 0.88, transition: { type: 'spring', stiffness: 650, damping: 18 } },
      }}
      {...rest}>
      <motion.span
        style={{ display: 'inline-block', transformOrigin: '50% 10%' }}
        variants={{
          rest: { y: 0 },
          hover: { y: [0, -2, 0], transition: { duration: 0.45, ease: 'easeOut' } },
        }}>
        <ShoppingCart size={24} strokeWidth={1.8} style={{ color: 'var(--text-color)' }} />
      </motion.span>
      <motion.span
        className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white leading-none"
        style={{ backgroundColor: 'var(--theme-color)' }}
        variants={{
          rest: { scale: 1 },
          hover: {
            scale: [1, 1.25, 1],
            transition: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] as const, delay: 0.05 },
          },
        }}>
        {totalCartQuantity}
      </motion.span>
    </motion.button>
  )
)
CartTriggerButton.displayName = 'CartTriggerButton'

export const Header = ({ className = '' }: HeaderProps) => {
  const navigate = useNavigate()
  const cartIsOpen = useCartStore(s => s.cartIsOpen)
  const setCartOpen = useCartStore(s => s.setCartOpen)
  const confirmCheckoutOpen = useCartStore(s => s.confirmCheckoutOpen)
  const setConfirmCheckoutOpen = useCartStore(s => s.setConfirmCheckoutOpen)
  const addUIMessage = useUIStore(s => s.addUIMessage)
  const { showHeader } = useEmbedConfig()
  const [isScrolled, setIsScrolled] = useState(false)
  const isCompact = useIsCompact()

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const sessionStore = useSessionStore()
  const {
    campaign,
    session,
    isSessionActive,
    isSessionValid,
    canLogoutSession: canLogout,
    isSessionUserKnown: userKnown,
    goHome,
  } = sessionStore

  const cartStore = useCartStore()
  const cartItems = cartStore.cart || []
  const totalCartQuantity = cartStore.cartItemCount()

  const inputState = useSessionStore(s => s.inputState)
  const isMatchingOrVerifying = inputState === InputState.NeedsMatch || inputState === InputState.NeedsVerification
  // During the match/verify pre-submit states the user has no session-level escape
  // (e.g. knows their email but not their last name) — surface logout so they're not
  // wedged. `canLogoutSession` is false while a session is still Unmatched.
  const canLogoutSession = canLogout() || isMatchingOrVerifying
  const isSessionUserKnown = userKnown()
  const showCart = cartItems.length > 0

  const isCustomerPortal = session && Object.values(CustomerPortalInputType).includes(session.step_type as any)

  const getLogo = () => (campaign as any)?.logo?.file || null
  const getMobileLogo = () => (campaign as any)?.mobile_logo?.file || getLogo()

  const handleGoHome = async () => {
    if (!isSessionActive() || !isSessionValid()) {
      return
    }
    try {
      await goHome()
      const target = currentSessionHome()
      if (window.location.pathname !== target) {
        navigate(target)
      } else {
        window.location.reload()
      }
    } catch (error) {
      // Actions abort if the WebSocket can't connect (usually a dead/expired
      // session). Refreshing won't recover it, so send the user to logout.
      console.error('Error going home:', error)
      const msg = 'Your session has expired. Please start again.'
      navigate(`/logout?msg=${encodeURIComponent(msg)}`, { state: { msg }, replace: true })
    }
  }

  const handleLogout = () => {
    navigate('/logout')
  }

  // Must stay below the hooks above to keep hook order stable.
  if (isEmbedded() && !showHeader) {
    return null
  }

  if (!campaign) {
    return null
  }

  // Custom header content
  if ((campaign as any)?.header_content) {
    return (
      <div className={`header-custom ${className}`}>
        <div dangerouslySetInnerHTML={{ __html: (campaign as any).header_content }} />
        <Language />
      </div>
    )
  }

  return (
    <div className={`header ${isCustomerPortal ? 'sticky-header' : ''} ${isScrolled ? 'scrolled' : ''} ${className}`}>
      <div className="section left">
        <Language />
      </div>

      <div className="section center">
        <div className="logo">
          {((campaign as any)?.logo || (campaign as any)?.mobile_logo) && (
            <button type="button" onClick={handleGoHome} className="btn-bare cursor-pointer">
              <img src={getMobileLogo()} alt={campaign.name} className="sm:hidden" />
              <img src={getLogo()} alt={campaign.name} className="hidden sm:block" />
            </button>
          )}
        </div>
      </div>

      <div className="section right">
        <div className="ml-auto flex items-center gap-3">
          {showCart && !isCompact && (
            <Popover.Root open={cartIsOpen} onOpenChange={setCartOpen}>
              <Popover.Trigger asChild>
                <CartTriggerButton totalCartQuantity={totalCartQuantity} />
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  className="hidden sm:block w-[380px] rounded-xl overflow-hidden z-50 cart-popover-content"
                  side="bottom"
                  sideOffset={12}
                  align="end"
                  onOpenAutoFocus={e => e.preventDefault()}
                  style={{
                    backgroundColor: 'var(--content-background-color)',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                  }}>
                  <Cart onClose={() => setCartOpen(false)} />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          )}

          {showCart && isCompact && (
            <CartTriggerButton totalCartQuantity={totalCartQuantity} onClick={() => setCartOpen(true)} />
          )}

          {isSessionUserKnown ? (
            <div className={`${showCart ? 'hidden sm:block' : ''}`}>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <motion.button
                    type="button"
                    className="btn-bare relative p-1.5 cursor-pointer no-transform outline-hidden"
                    aria-label="Account"
                    initial="rest"
                    animate="rest"
                    whileHover="hover"
                    whileTap="tap"
                    variants={{
                      rest: { scale: 1 },
                      hover: { scale: 1.08, transition: { type: 'spring', stiffness: 500, damping: 18 } },
                      tap: { scale: 0.9, transition: { type: 'spring', stiffness: 650, damping: 18 } },
                    }}>
                    <motion.span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
                      variants={{
                        rest: { y: 0 },
                        hover: {
                          y: [0, -3, 1, 0],
                          transition: { duration: 0.45, ease: 'easeInOut' },
                        },
                      }}>
                      <User size={24} strokeWidth={1.8} style={{ color: 'var(--text-color)' }} />
                      <span
                        className="notranslate text-base font-medium hidden md:inline"
                        style={{ color: 'var(--text-color)' }}>
                        {(session?.customer as any)?.first_name}
                      </span>
                    </motion.span>
                  </motion.button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content className="user-dropdown-menu" sideOffset={8} align="end">
                    <div className="user-dropdown-name sm:hidden" style={{ color: 'var(--light-text-color)' }}>
                      {(session?.customer as any)?.first_name} {(session?.customer as any)?.last_name}
                    </div>
                    <DropdownMenu.Item className="logout-btn no-transform" onSelect={handleLogout}>
                      <LogOut className="icon" />
                      <span>Logout</span>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          ) : canLogoutSession ? (
            <div className={`${showCart ? 'hidden sm:block' : ''}`}>
              {isCompact ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="logout-icon-btn no-transform"
                  aria-label="Log out">
                  <LogOut size={20} />
                </button>
              ) : (
                <button type="button" onClick={handleLogout} className="logout-btn no-transform">
                  <LogOut className="icon" />
                  <span>LOG OUT</span>
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
      {/* Mobile Cart — BottomSheet (portal-rendered, so gate with JS not CSS) */}
      {isCompact && (
        <BottomSheet isOpen={cartIsOpen} onClose={() => setCartOpen(false)} title="Your Cart">
          <Cart onClose={() => setCartOpen(false)} hideHeader />
        </BottomSheet>
      )}

      {confirmCheckoutOpen && (
        <CheckoutCustomerConfirmation
          onClose={() => setConfirmCheckoutOpen(false)}
          onVerified={() => {
            setConfirmCheckoutOpen(false)
            navigate('/step/checkout')
          }}
          onVerificationFailed={msg => addUIMessage({ type: 'error', message: msg })}
        />
      )}
    </div>
  )
}

export default Header
